import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { SearchPalette, type PaletteItem } from './SearchPalette'
import { SidebarSearchItem } from './SidebarSearchItem'
import { usePaletteSearch, type LocalPaletteSource, type RemotePaletteSource } from '../hooks/usePaletteSearch'

interface SearchPaletteContextValue {
    open: () => void
    close: () => void
    isOpen: boolean
}

const SearchPaletteContext = createContext<SearchPaletteContextValue>({
    open: () => undefined,
    close: () => undefined,
    isOpen: false,
})

/** Opens and closes the surrounding `SearchPaletteProvider`'s palette, e.g. from a sidebar search row. */
export const useSearchPalette = () => useContext(SearchPaletteContext)

/** Sidebar row that opens the surrounding provider's palette. */
export const SearchPaletteSidebarItem = () => {
    const { open } = useSearchPalette()
    return <SidebarSearchItem onClick={open} />
}

export interface SearchPaletteProviderProps {
    children: ReactNode
    /** While false, ⌘K does nothing and an open palette closes */
    enabled?: boolean
    localSources: LocalPaletteSource[]
    remoteSources: RemotePaletteSource[]
    placeholder?: string
    label?: string
    emptyHint?: string
    /** Entry offered after the results for any non-empty query, such as "Ask Assistant" */
    footerItem?: (query: string) => PaletteItem | undefined
    /** Changing this, e.g. on an organization switch, closes the palette and discards results and searches */
    resetKey?: string
    /** Called with each chosen entry, before its target runs */
    onSelect?: (item: PaletteItem) => void
}

/**
 * Global search palette opened with ⌘K (Ctrl+K off macOS). Navigation targets go through the app's router, so route
 * guards such as unsaved-changes prompts still apply.
 */
export const SearchPaletteProvider = ({
    children,
    enabled = true,
    localSources,
    remoteSources,
    placeholder,
    label,
    emptyHint,
    footerItem,
    resetKey,
    onSelect,
}: SearchPaletteProviderProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [query, setQuery] = useState('')
    const navigate = useNavigate()
    const { groups, loading } = usePaletteSearch(query, { localSources, remoteSources, scope: resetKey })

    const open = useCallback(() => {
        if (enabled) setIsOpen(true)
    }, [enabled])
    const close = useCallback(() => setIsOpen(false), [])

    useEffect(() => {
        if (!isOpen) setQuery('')
    }, [isOpen])

    useEffect(() => {
        if (!enabled) return
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
                event.preventDefault()
                setIsOpen((wasOpen) => !wasOpen)
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [enabled])

    useEffect(() => {
        if (!enabled) close()
    }, [enabled, close])

    useEffect(() => {
        close()
    }, [resetKey, close])

    const select = (item: PaletteItem) => {
        onSelect?.(item)
        // Close first, so a navigation guard's confirmation does not open underneath the palette.
        close()
        const { target } = item
        switch (target.kind) {
            case 'navigate':
                void navigate(target.to, target.state === undefined ? undefined : { state: target.state })
                break
            case 'href':
                if (target.newTab) window.open(target.href, '_blank', 'noopener')
                else window.location.assign(target.href)
                break
            case 'run':
                target.run()
                break
        }
    }

    const contextValue = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen])

    return (
        <SearchPaletteContext.Provider value={contextValue}>
            {children}
            <SearchPalette
                open={isOpen}
                onClose={close}
                query={query}
                onQueryChange={setQuery}
                groups={groups}
                loading={loading}
                onSelect={select}
                footerItem={footerItem?.(query)}
                placeholder={placeholder}
                label={label}
                emptyHint={emptyHint}
            />
        </SearchPaletteContext.Provider>
    )
}
